/**
 * 📋 Barcode-Stapel (Sonderfunktion „batchBarcodes", z. B. Green Goat)
 *
 * Beim Erfassen werden die ECHTEN Barcodes aus dem Kamerafoto gelesen (nicht die per KI
 * gelesene Sendungsnummer — die weicht z. B. bei DPD vom Barcode-Inhalt ab). Nach einer
 * Kurierlieferung zeigt die Stapel-Ansicht alle Codes nacheinander groß an, damit das
 * Zusteller-Gerät sie abscannen kann, ohne jedes Paket erneut anzufassen.
 *
 * Bewusst getrennt vom Paket-Sync (eigener localStorage-Speicher) — kann den Sync nie stören.
 * Reine Funktionen → per Node testbar.
 */

import { SERVER_URL } from '../config/serverConfig';

const KEY = 'kiosk_batch_codes';
const MAX = 400;

// ---------------------------------------------------------------------------
// Auswahl des besten Codes eines Labels
// ---------------------------------------------------------------------------
const EINDIM = new Set(['code_128', 'code_39', 'code_93', 'codabar', 'ean_13', 'ean_8', 'itf', 'upc_a', 'upc_e']);

export function normFormat(f) {
  return String(f || '').toLowerCase().replace(/[\s-]+/g, '_').replace(/^code128$/, 'code_128')
    .replace(/^datamatrix$/, 'data_matrix').replace(/^qrcode$/, 'qr_code');
}

const ziffern = (s) => String(s || '').replace(/\D/g, '');
const alnum = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
// Nur wenn der Paketdienst sicher DHL / Deutsche Post ist, gelten die DHL-Leitcode-Heuristiken (leer/unbekannt → neutral)
const istDhlDienst = (dienst) => /^(dhl|deutsche\s*post)/i.test(String(dienst || '').trim());

// ---------------------------------------------------------------------------
// 🔍 DHL-Prüfziffer, Leitcode-Filter & Doppelabgleich (Barcode ↔ OCR)
// ---------------------------------------------------------------------------

/**
 * GS1 Modulo-10 Prüfziffer (Gewichte 3 und 1 abwechselnd von rechts, ohne Prüfziffer).
 * Gilt für 20-stellige DHL-NVE/SSCC-Sendungsnummern („0034…") bzw. 18-stellige ohne „00".
 */
export function pruefeGs1Mod10(s) {
  const d = String(s || '');
  if (!/^\d{18}$|^\d{20}$/.test(d)) return false;
  let sum = 0;
  for (let i = d.length - 2, pos = 0; i >= 0; i--, pos++) {
    sum += (d.charCodeAt(i) - 48) * (pos % 2 === 0 ? 3 : 1);
  }
  const chk = (10 - (sum % 10)) % 10;
  return chk === (d.charCodeAt(d.length - 1) - 48);
}

/**
 * Deutsche Post / DHL Identcode Modulo-10 Prüfziffer (Gewichte 4 und 9 abwechselnd von rechts).
 * Gilt für 12-stellige DHL-Identcodes (z. B. „35.010.294735 9" → „350102947359").
 */
export function pruefeDhlIdentMod10(s) {
  const d = String(s || '');
  if (!/^\d{12}$/.test(d)) return false;
  let sum = 0;
  for (let i = d.length - 2, pos = 0; i >= 0; i--, pos++) {
    sum += (d.charCodeAt(i) - 48) * (pos % 2 === 0 ? 4 : 9);
  }
  const chk = (10 - (sum % 10)) % 10;
  return chk === (d.charCodeAt(d.length - 1) - 48);
}

/**
 * Internationale UPU-S10-Prüfziffer (z. B. „CY123456785DE", „CP…", „RR…", „EE…", „LA…"):
 * 2 Buchstaben + 8 Ziffern + 1 Prüfziffer (Modulo 11 mit Gewichten 8,6,4,2,3,5,9,7) + 2 Länderbuchstaben.
 */
export function pruefeUpuS10(s) {
  const an = alnum(s);
  const m = an.match(/^[A-Z]{2}(\d{8})(\d)[A-Z]{2}$/);
  if (!m) return false;
  const w = [8, 6, 4, 2, 3, 5, 9, 7];
  let sum = 0;
  for (let i = 0; i < 8; i++) sum += (m[1].charCodeAt(i) - 48) * w[i];
  const r = 11 - (sum % 11);
  const chk = r === 10 ? 0 : (r === 11 ? 5 : r);
  return chk === (m[2].charCodeAt(0) - 48);
}

/**
 * UPS 1Z-Sendungsnummer Prüfziffer (18 Zeichen: „1Z" + 15 alphanumerische Zeichen + 1 Modulo-10-Prüfziffer).
 */
export function pruefeUps1Z(s) {
  const an = alnum(s);
  if (!/^1Z[A-Z0-9]{16}$/.test(an)) return false;
  const body = an.slice(2, 17);
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const code = body.charCodeAt(i);
    const val = (code >= 48 && code <= 57) ? (code - 48) : ((code - 63) % 10);
    sum += val * (i % 2 === 0 ? 1 : 2);
  }
  const chk = (10 - (sum % 10)) % 10;
  return chk === (an.charCodeAt(17) - 48);
}

/**
 * Entfernt führende GS1-/AIM-Symbologie-Steuerzeichen (z. B. „]C1", „]C0", „]d2", „]e0", „]Q3").
 */
export function stripGs1Prefix(s) {
  return String(s || '').trim().replace(/^(\]C[01]|\]d2|\]e0|\]Q3)+/i, '').trim();
}

/**
 * Prüft eine (ggf. formatierte) Sendungsnummer auf gültige DHL-/UPU-S10-/UPS-Prüfziffer und normalisiert sie.
 * Repariert auch 18-stellige „3404…"-Nummern (denen am Anfang „00" fehlt) zu 20-stelligen „003404…".
 */
export function pruefeDhlSendungsnummer(tracking) {
  const raw = stripGs1Prefix(tracking);
  const dgt = ziffern(raw);
  const an = alnum(raw);
  if (!an) {
    return { isNumericDhl: false, valid: false, normalized: raw };
  }
  // Internationaler UPU-S10-Code (z. B. CY123456785PL)
  if (/^[A-Z]{2}\d{9}[A-Z]{2}$/.test(an)) {
    return { isNumericDhl: false, valid: pruefeUpuS10(an), normalized: an, kind: 'upu_s10' };
  }
  // UPS 1Z-Sendungsnummer (18 Zeichen mit Modulo-10-Prüfziffer)
  if (/^1Z[A-Z0-9]{16}$/.test(an)) {
    return { isNumericDhl: false, valid: pruefeUps1Z(an), normalized: an, kind: 'ups_1z' };
  }
  if (!dgt || dgt !== an) {
    return { isNumericDhl: false, valid: false, normalized: raw };
  }
  if (dgt.length === 20 && dgt.startsWith('00')) {
    return { isNumericDhl: true, valid: pruefeGs1Mod10(dgt), normalized: dgt, kind: 'nve20' };
  }
  if (dgt.length === 18 && dgt.startsWith('34')) {
    const ok = pruefeGs1Mod10(dgt);
    return { isNumericDhl: true, valid: ok, normalized: ok ? '00' + dgt : dgt, kind: 'nve18' };
  }
  if (dgt.length === 12) {
    return { isNumericDhl: true, valid: pruefeDhlIdentMod10(dgt), normalized: dgt, kind: 'ident12' };
  }
  // 10-stellige DHL Express Airwaybill (Waybill, Modulo-7-Prüfziffer)
  if (dgt.length === 10) {
    const ok = Number(dgt.slice(0, 9)) % 7 === Number(dgt[9]);
    return { isNumericDhl: true, valid: ok, normalized: dgt, kind: 'dhl_express_awb' };
  }
  // Zu kurze reine Ziffernfolgen (< 10 Stellen, z. B. 8-stellige Produkt-/Steuercodes wie „96993017" oder „01651419")
  // oder typische Fehllesung (z. B. 14-stelliger Leitcode/Abrechnungsnr. oder 19/21-stellige „0034…"-Lesefehler)
  if (dgt.length < 10 || (dgt.length >= 13 && dgt.length <= 22) || dgt.startsWith('0034') || dgt.startsWith('3404')) {
    return { isNumericDhl: true, valid: false, normalized: dgt, kind: 'ungueltig' };
  }
  return { isNumericDhl: false, valid: false, normalized: raw };
}

/**
 * Erkennt Leitcodes (Routingcodes), abgeschnittene „0034…"-Lesefehler und kurze Produkt-/Steuercodes
 * auf deutschen/internationalen Paketlabels, die NIEMALS als Sendungsnummer oder Stapel-Barcode verwendet werden dürfen:
 * - GS1-128 Routingcode mit AI (403): „(403)27681549+9900…" bzw. „]C1403…"
 * - DHL Express IATA Routingcode mit DI (2L): „(2L)NL3051xm+51000000" bzw. „2LNL3051XM+51000000"
 * - Klassischer 14-stelliger ITF-Leitcode (beginnt mit 5-stelliger PLZ, Stellen 12–13 meist „00")
 * - 14-stellige DHL-Abrechnungsnummern (beginnen mit 50, 60, 62, 63)
 * - Reine Ziffernfolgen < 10 Stellen (z. B. „]C196993017", „01651419", „815490")
 * - Abgeschnittene/fehlerhafte DHL-NVE-Codes mit „0034…" (z. B. 19 statt 20 Ziffern)
 *
 * Die reinen Ziffern-Heuristiken (< 10 Stellen, 14-stellig, PLZ-Präfix) gelten NUR bei bekanntem Paketdienst DHL —
 * sonst würden z. B. 8-stellige Mondial-Relay- oder 14-stellige Hermes-/DPD-Codes fälschlich verworfen.
 */
export function istLeitcode(text, format = '', plz = '', dienst = '') {
  const raw = String(text || '').trim();
  if (!raw) return true;
  // GS1 Routingcode AI (403) oder DHL Express IATA Routingcode DI (2L)
  if (
    raw.startsWith('(403)') || raw.startsWith(']C1403') || raw.startsWith(']C1(403)') ||
    /^(\]C1)?\(?403\)?\d{3}[0-9A-Z+]{5,}/i.test(raw) ||
    /^(\]C1)?\(?2L\)?[A-Z]{2}[0-9A-Z]{3,10}\+?/i.test(raw)
  ) {
    return true;
  }
  const t = stripGs1Prefix(raw);
  const an = alnum(t);
  if (an.length < 8) return true;
  const dgt = ziffern(t);
  const f = normFormat(format);
  // Abgeschnittene oder prüfziffer-falsche deutsche DHL-NVE („0034…" muss exakt 20 Ziffern + gültige Mod-10 haben)
  if (dgt === an && dgt.startsWith('0034') && (dgt.length !== 20 || !pruefeGs1Mod10(dgt))) return true;
  if (dgt === an && dgt.startsWith('3404') && (dgt.length !== 18 || !pruefeGs1Mod10(dgt))) return true;

  // Ab hier nur DHL-spezifische Ziffern-Heuristiken — bei unbekanntem/anderem Paketdienst nichts verwerfen
  if (!istDhlDienst(dienst)) return false;
  // Reine Zifferncodes unter 10 Stellen (z. B. 8-stellige Produkt-/Routencodes wie „96993017" oder „01651419") sind nie DHL-Sendungsnummern
  if (dgt === an && dgt.length < 10) return true;

  const plz5 = (String(plz || '').match(/\b(\d{5})\b/) || [])[1] || '';
  if (dgt.length === 14 && dgt === an) {
    // 14-stelliger ITF-Code auf deutschen Labels ist immer der Leitcode
    if (f === 'itf') return true;
    // Beginnt mit der 5-stelligen Empfänger-PLZ → Leitcode
    if (plz5 && dgt.startsWith(plz5)) return true;
    // Typisches deutsches Leitcode-Muster: Produktcode „00", „01", „07", „53", „54" an Stelle 12–13
    if (/^(00|01|07|53|54)$/.test(dgt.slice(11, 13))) return true;
    // 14-stellige DHL-Abrechnungsnummer
    if (/^(50|60|62|63)\d{12}$/.test(dgt)) return true;
  }
  // 13- bis 15-stellige reine Zifferncodes, die direkt mit der 5-stelligen Empfänger-PLZ beginnen, sind Leitcodes
  if (plz5 && dgt === an && dgt.length >= 13 && dgt.length <= 15 && dgt.startsWith(plz5)) {
    return true;
  }
  return false;
}

/**
 * Rekonstruiert den 28-stelligen DPD-1D-Strichcode („%03045RD09447436487678345528")
 * aus dem 2D-Aztec-Code auf DPD-Labels („[)>\x1e01\x1d02\x1d3045RD\x1d528\x1d345\x1d09447436487678\x1d…").
 */
export function extrahiereDpdBarcodeAusAztec(text) {
  const s = String(text || '').replace(/␞/g, '\x1e').replace(/␝/g, '\x1d');
  if (!s.startsWith('[)>')) return null;
  const parts = s.split('\x1d');
  if (parts.length < 6) return null;
  const plz = String(parts[2] || '').trim().toUpperCase().replace(/\s+/g, '');
  const land = String(parts[3] || '').trim();
  const service = String(parts[4] || '').trim();
  const parcel = String(parts[5] || '').trim();
  if (/^[0-9A-Z]{4,7}$/.test(plz) && /^\d{3}$/.test(land) && /^\d{3}$/.test(service) && /^\d{14}$/.test(parcel)) {
    return '%' + plz.padStart(7, '0') + parcel + service + land;
  }
  return null;
}

/**
 * Extrahiert eine verifizierte Sendungsnummer aus einem gelesenen Barcode (1D oder 2D).
 * Gibt null zurück, wenn es ein Leitcode oder ein unbekannter Datenblock ist.
 */
export function extrahiereSendungsnummerAusBarcode(code, plz = '', dienst = '') {
  if (!code || !code.text) return null;
  const raw = String(code.text).replace(/\u0000/g, '').trim();
  const f = normFormat(code.format);
  if (istLeitcode(raw, f, plz, dienst)) return null;
  const t = stripGs1Prefix(raw);

  // 1) DHL-DMC DataMatrix: „DHLDMC|00340434784304663887"
  const mDmc = t.match(/^DHLDMC\|(\d{12,20})$/i);
  if (mDmc) {
    const chk = pruefeDhlSendungsnummer(mDmc[1]);
    return chk.valid ? chk.normalized : null;
  }
  // 2) Deutsche Post / DHL Frankier-DataMatrix: „67e3212b|SHA256…|V1.0|358629240571|70895000000009||"
  const mDv = t.match(/\|V1\.0\|(\d{12})\|/);
  if (mDv) {
    const chk = pruefeDhlSendungsnummer(mDv[1]);
    return chk.valid ? chk.normalized : mDv[1];
  }
  // 3) GS1-128 NVE / SSCC: „(00)340434288404966126" oder „]C100340434288404966126"
  const mGs1 = t.match(/^\(?00\)?(\d{18})$/);
  if (mGs1) {
    const cand = '00' + mGs1[1];
    return pruefeGs1Mod10(cand) ? cand : null;
  }
  // 3b) DPD 27/28-stelliger Code 128 („%03045RD09447436487678345528[0]") oder DPD Aztec → 14-stellige Paketnummer (nicht bei explizitem DHL-Dienst)
  if (!istDhlDienst(dienst)) {
    const mDpd = t.match(/^%?[0-9A-Z]{7}(\d{14})\d{5,6}[0-9A-Z]$/i);
    if (mDpd) return mDpd[1];
    const dpdAztec = extrahiereDpdBarcodeAusAztec(raw);
    if (dpdAztec) return dpdAztec.slice(8, 22);
  }
  // 4) Reiner Zifferncode (10, 12, 18 oder 20 Stellen DHL), UPU-S10 oder UPS 1Z
  const dgt = ziffern(t);
  const an = alnum(t);
  if (/^[A-Z]{2}\d{9}[A-Z]{2}$/.test(an) && pruefeUpuS10(an)) {
    return an;
  }
  if (/^1Z[A-Z0-9]{16}$/.test(an) && pruefeUps1Z(an)) {
    return an;
  }
  if (dgt === an && (dgt.length === 10 || dgt.length === 12 || dgt.length === 18 || dgt.length === 20)) {
    const chk = pruefeDhlSendungsnummer(dgt);
    if (chk.valid) return chk.normalized;
  }
  // 5) Sonstiger 1D-Sendungscode anderer/internationaler Paketdienste
  if (EINDIM.has(f) && an.length >= 8 && an.length <= 34) {
    // Falls es wie eine 0034-Nummer aussieht, aber Länge/Prüfziffer falsch ist → ablehnen
    if (/^0034\d+$/.test(an) && (an.length !== 20 || !pruefeGs1Mod10(an))) return null;
    if (dgt === an && dgt.length < 10) return null;
    return t;
  }
  return null;
}

/**
 * Klassifiziert einen oder mehrere im Live-Sucher / Nachscan-Foto erkannte Barcodes sofort für das
 * Rot/Grün-Ampel-Feedback („Etikett um die Ecke"):
 * - Wenn mindestens eine echte Sendungsnummer im Bild ist → { status: 'sendung', tracking, code } (GRÜN)
 * - Wenn nur ein Leitcode / Routingcode / kurzer Steuercode im Bild ist → { status: 'leitcode', raw } (ROT)
 * - Sonst → { status: 'leer' }
 */
export function klassifiziereScanCodes(codes = [], plz = '', dienst = '') {
  const list = Array.isArray(codes) ? codes : [];
  // Unbekannter Paketdienst ('') ist NICHT automatisch DHL → neutral, kein ROT für fremde Codes
  const istDhl = istDhlDienst(dienst);
  const sendungsTreffer = [];
  let ersterLeitcode = '';

  for (const c of list) {
    if (!c || !c.text) continue;
    const raw = String(c.text).replace(/\u0000/g, '').trim();
    const fmt = normFormat(c.format || 'code_128');
    if (!raw) continue;

    // Bei DHL sind 14-stellige reine Zifferncodes IMMER Leitcodes/Abrechnungsnummern (nie Sendungsnummern!)
    const dgt = ziffern(stripGs1Prefix(raw));
    const an = alnum(stripGs1Prefix(raw));
    if (istLeitcode(raw, fmt, plz, dienst) || (istDhl && dgt === an && dgt.length === 14)) {
      if (!ersterLeitcode && an.length >= 6) ersterLeitcode = stripGs1Prefix(raw);
      continue;
    }

    const extr = extrahiereSendungsnummerAusBarcode({ text: raw, format: fmt }, plz, dienst);
    if (extr) {
      const chk = pruefeDhlSendungsnummer(extr);
      sendungsTreffer.push({
        tracking: chk.valid ? chk.normalized : extr,
        code: { text: chk.valid && EINDIM.has(fmt) ? chk.normalized : stripGs1Prefix(raw), format: fmt },
        pruefzifferOk: chk.valid,
      });
    } else if (!ersterLeitcode && an.length >= 6) {
      ersterLeitcode = stripGs1Prefix(raw);
    }
  }

  if (sendungsTreffer.length > 0) {
    // Prüfziffer-geprüfte Sendungsnummern bevorzugen, falls mehrere Barcodes gleichzeitig im Bild sind
    sendungsTreffer.sort((a, b) => (b.pruefzifferOk ? 1 : 0) - (a.pruefzifferOk ? 1 : 0));
    return {
      status: 'sendung',
      tracking: sendungsTreffer[0].tracking,
      code: sendungsTreffer[0].code,
      pruefzifferOk: sendungsTreffer[0].pruefzifferOk,
    };
  }

  if (ersterLeitcode) {
    return {
      status: 'leitcode',
      raw: ersterLeitcode,
    };
  }

  return { status: 'leer' };
}

/**
 * Doppelabgleich zwischen allen im Foto erkannten Barcodes und der per OCR gelesenen Sendungsnummer.
 * Filtert Leitcodes automatisch heraus und korrigiert OCR-Lesefehler anhand des Barcodes.
 *
 * Status-Werte:
 * - 'verifiziert'        : Barcode im Foto gelesen UND stimmt mit OCR-Sendungsnummer überein (100 % sicher)
 * - 'korrigiert'         : Barcode im Foto gelesen (Prüfziffer gültig) und hat OCR-Lesefehler korrigiert (100 % sicher)
 * - 'pruefziffer_ok'     : Kein Barcode im Foto lesbar, aber OCR-Sendungsnummer hat gültige DHL-/S10-Prüfziffer
 * - 'manuell_bestaetigt' : Vom Nutzer im Fallback („Was ist die Sendungsnummer?") manuell bestätigt/eingegeben
 * - 'unsicher'           : Weder Barcode-Übereinstimmung noch gültige Prüfziffer — Nutzer im Fallback fragen!
 */
export function doppelabgleichSendung({ codes = [], tracking = '', plz = '', manuellBestaetigt = false, dienst = '' } = {}) {
  const rawList = Array.isArray(codes) ? codes : [];
  let leitcodesGefiltert = 0;
  const bcTrackings = [];
  const sichereBcTrackings = new Set();

  let ocrRaw = stripGs1Prefix(tracking);
  const ocrAn = alnum(ocrRaw);
  const mDpdOcr28 = ocrAn.match(/^%?[0-9A-Z]{7}(\d{14})\d{6}[A-Z0-9]?$/i);
  if (mDpdOcr28) {
    ocrRaw = mDpdOcr28[1];
  } else if (/^dpd$/i.test(String(dienst || '').trim()) && /^\d{14}[A-Z0-9]$/i.test(ocrAn)) {
    ocrRaw = ocrAn.slice(0, 14);
  }
  const ocrChk = pruefeDhlSendungsnummer(ocrRaw);
  // Ohne expliziten Paketdienst: rein numerische OCR-Sendungsnummer → DHL-Abgleich wie bisher, sonst neutral
  const dienstEff = dienst || (ocrChk.isNumericDhl ? 'DHL' : '');
  const dienstLow = String(dienstEff || '').trim().toLowerCase();

  for (const c of rawList) {
    if (!c || !c.text) continue;
    if (istLeitcode(c.text, c.format, plz, dienstEff)) {
      if (alnum(c.text).length >= 8) leitcodesGefiltert++;
      continue;
    }
    const extr = extrahiereSendungsnummerAusBarcode(c, plz, dienstEff);
    if (extr) {
      if (!bcTrackings.includes(extr)) bcTrackings.push(extr);
      const rawClean = stripGs1Prefix(String(c.text).replace(/\u0000/g, '').trim());
      const istDpdBc = /^%?[0-9A-Z]{7}\d{19,20}[0-9A-Z]$/i.test(rawClean) || Boolean(extrahiereDpdBarcodeAusAztec(rawClean));
      const istUpsBc = /^1Z[A-Z0-9]{16}$/i.test(extr) && pruefeUps1Z(extr);
      if ((istDpdBc && (!dienstLow || dienstLow === 'dpd')) || (istUpsBc && (!dienstLow || dienstLow === 'ups'))) {
        sichereBcTrackings.add(extr);
      }
    }
  }

  const ocrIstLeit = ocrRaw ? istLeitcode(ocrRaw, '', plz, dienstEff) : false;
  const ocrNorm = ocrChk.valid ? ocrChk.normalized : (ocrChk.isNumericDhl ? ziffern(ocrRaw) : ocrRaw);

  // Kandidatenliste für das Fallback-Auswahlfeld (ohne Duplikate)
  const kandidaten = [];
  const addKand = (val, quelle) => {
    const v = stripGs1Prefix(val);
    if (!v || istLeitcode(v, '', plz, dienstEff)) return;
    if (!kandidaten.some((k) => alnum(k.value) === alnum(v))) {
      kandidaten.push({ value: v, quelle });
    }
  };
  for (const b of bcTrackings) addKand(b, 'barcode');
  if (ocrRaw && !ocrIstLeit) addKand(ocrNorm || ocrRaw, 'ocr');

  if (manuellBestaetigt && ocrRaw.length >= 4) {
    return {
      status: 'manuell_bestaetigt',
      tracking: ocrChk.valid ? ocrChk.normalized : ocrRaw,
      ocrTracking: ocrRaw,
      pruefzifferOk: ocrChk.valid,
      barcodeGefunden: bcTrackings.length > 0,
      leitcodesGefiltert,
      kandidaten,
    };
  }

  if (bcTrackings.length > 0) {
    // Prüfen, ob einer der gelesenen Barcodes zur OCR-Nummer passt
    const passender = !ocrIstLeit && bcTrackings.find((b) => {
      const bAn = alnum(b);
      const oAn = alnum(ocrNorm);
      return bAn && oAn && (bAn === oAn || bAn.includes(oAn) || oAn.includes(bAn));
    });
    if (passender) {
      return {
        status: alnum(passender) === alnum(ocrRaw) ? 'verifiziert' : 'korrigiert',
        tracking: passender,
        ocrTracking: ocrRaw,
        pruefzifferOk: true,
        barcodeGefunden: true,
        leitcodesGefiltert,
        kandidaten,
      };
    }
    // Barcode weicht von OCR ab:
    // Wenn der Barcode eine gültige DHL-/UPU-S10-Prüfziffer hat (oder OCR leer/ungültig war und genau 1 Barcode existiert),
    // gewinnt der Barcode automatisch. Bei exotischen/internationalen Codes ohne bekannte Prüfziffer → nachfragen!
    const besterBc = bcTrackings.find((b) => pruefeDhlSendungsnummer(b).valid)
      || bcTrackings.find((b) => sichereBcTrackings.has(b))
      || bcTrackings[0];
    const bcChk = pruefeDhlSendungsnummer(besterBc);
    const istSicherBc = bcChk.valid || sichereBcTrackings.has(besterBc);
    const ocrUngueltig = !ocrRaw || ocrIstLeit || (ocrChk.isNumericDhl && !ocrChk.valid);
    if (istSicherBc || (ocrUngueltig && bcTrackings.length === 1)) {
      return {
        status: ocrRaw ? 'korrigiert' : 'verifiziert',
        tracking: bcChk.valid ? bcChk.normalized : besterBc,
        ocrTracking: ocrRaw,
        pruefzifferOk: istSicherBc,
        barcodeGefunden: true,
        leitcodesGefiltert,
        kandidaten,
      };
    }
    return {
      status: 'unsicher',
      tracking: (!ocrUngueltig && ocrRaw) ? ocrRaw : besterBc,
      ocrTracking: ocrRaw,
      pruefzifferOk: false,
      barcodeGefunden: true,
      leitcodesGefiltert,
      kandidaten,
    };
  }

  // Kein Sendungs-Barcode im Foto erkannt → Prüfziffer der OCR-Nummer auswerten
  if (ocrChk.valid) {
    return {
      status: 'pruefziffer_ok',
      tracking: ocrChk.normalized,
      ocrTracking: ocrRaw,
      pruefzifferOk: true,
      barcodeGefunden: false,
      leitcodesGefiltert,
      kandidaten,
    };
  }

  return {
    status: 'unsicher',
    tracking: ocrIstLeit ? '' : ocrRaw,
    ocrTracking: ocrRaw,
    pruefzifferOk: false,
    barcodeGefunden: false,
    leitcodesGefiltert,
    kandidaten,
  };
}

/**
 * Reihenfolge: 1D-Code, der die Sendungsnummer enthält → 2D-Code, der sie enthält →
 * irgendein Code 128 → irgendein 1D → kürzester 2D-Code.
 * @param {Array<{text:string, format:string}>} codes
 * @returns {number} Index des besten Codes, -1 wenn keiner
 */
export function besterCodeIndex(codes, tracking) {
  const list = (codes || []).map((c) => ({ ...c, format: normFormat(c.format) }));
  if (!list.length) return -1;
  const trk = ziffern(tracking);
  const trkAlnum = alnum(tracking);
  const enthaelt = (c) => {
    if (!trkAlnum || trkAlnum.length < 6) return false;
    const t = alnum(c.text);
    return t.includes(trkAlnum) || (trk.length >= 8 && ziffern(c.text).includes(trk));
  };
  const idx = (pred) => list.findIndex(pred);
  const kandidaten = [
    (c) => EINDIM.has(c.format) && enthaelt(c),
    (c) => !EINDIM.has(c.format) && enthaelt(c),
    (c) => c.format === 'code_128',
    (c) => EINDIM.has(c.format),
  ];
  for (const k of kandidaten) { const i = idx(k); if (i >= 0) return i; }
  let best = 0;
  list.forEach((c, i) => { if (String(c.text).length < String(list[best].text).length) best = i; });
  return best;
}

/** Formate, die die Stapel-Ansicht selbst wieder erzeugen kann (1D → als Code 128) */
export function istDarstellbar(format) {
  const f = normFormat(format);
  return EINDIM.has(f) || f === 'data_matrix' || f === 'qr_code';
}

/**
 * Doppelte (gleicher Inhalt UND Format), Leitcodes ((403)…, 14-stellige ITF-Leitcodes …)
 * sowie nicht darstellbare Codes (Aztec, PDF417 …) entfernen.
 */
export function dedupeCodes(codes, plz = '', dienst = '') {
  const seen = new Set();
  const out = [];
  for (const c of codes || []) {
    // Nullzeichen, die manche Scanner anhängen, raus — sonst schlägt jeder Vergleich fehl (23.10.2026, R10)
    let text = String((c && c.text) || '').replace(/\u0000/g, '');
    let fmt = normFormat(c && c.format);
    if (fmt === 'aztec') {
      const dpdBc = extrahiereDpdBarcodeAusAztec(text);
      if (dpdBc) {
        text = dpdBc;
        fmt = 'code_128';
      }
    }
    if (!text || !istDarstellbar(fmt) || istLeitcode(text, fmt, plz, dienst)) continue;
    if (EINDIM.has(fmt)) text = stripGs1Prefix(text);
    const key = fmt + '|' + text;
    if (!text || seen.has(key)) continue;
    seen.add(key);
    out.push({ text, format: fmt });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Stapel-Speicher (lokal + Account-Sync über alle Geräte inkl. Fotostation)
// ---------------------------------------------------------------------------
const KEY_POS = 'kiosk_batch_pos';
const KEY_HIST = 'kiosk_batch_history';
const KEY_EXPECTED = 'kiosk_batch_expected';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Maximaler zeitlicher Abstand (15 Minuten) zwischen zwei nacheinander erfassten Paketen,
 * damit sie automatisch im selben Liefer-Stapel landen. Liegt das letzte Paket länger als
 * 15 Minuten (Stunden oder Tage) zurück, wird automatisch ein neuer eigener Stapel begonnen.
 */
export const BATCH_GAP_MS = 15 * 60 * 1000;

let pendingDeletedHistoryIds = new Set();
// Bewusst aus dem aktiven Stapel entfernte Einträge (removeFromBatch) → Server löscht sie gezielt per removeIds
let pendingRemovedIds = new Set();

function loadRawActive() {
  try { const a = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(a) ? a : []; } catch { return []; }
}

function loadHistoryRaw() {
  try {
    const a = JSON.parse(localStorage.getItem(KEY_HIST) || '[]');
    if (!Array.isArray(a)) return [];
    const cutoff = Date.now() - SEVEN_DAYS_MS;
    return a.filter((h) => h && Array.isArray(h.items) && (h.finishedAt || 0) >= cutoff);
  } catch { return []; }
}

function saveHistoryRaw(hist) {
  try {
    const cutoff = Date.now() - SEVEN_DAYS_MS;
    const clean = (hist || []).filter((h) => h && Array.isArray(h.items) && (h.finishedAt || 0) >= cutoff).slice(0, 60);
    localStorage.setItem(KEY_HIST, JSON.stringify(clean));
  } catch { /* */ }
}

export function getBatchExpectedCount() {
  try {
    const n = parseInt(localStorage.getItem(KEY_EXPECTED) || '0', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch { return 0; }
}

export function setBatchExpectedCount(count, sync = true) {
  const clean = Math.max(0, Number.isFinite(Number(count)) ? Math.floor(Number(count)) : 0);
  try {
    if (clean > 0) localStorage.setItem(KEY_EXPECTED, String(clean));
    else localStorage.removeItem(KEY_EXPECTED);
  } catch { /* */ }
  notifyChanged(loadRawActive());
  if (sync) pushBatchToServer();
}

/**
 * Teilt eine chronologisch sortierte Liste von Stapel-Einträgen anhand der 15-Minuten-Lücke
 * (BATCH_GAP_MS) in getrennte Liefer-Sitzungen auf.
 */
export function splitItemsByTimeGap(items, gapMs = BATCH_GAP_MS, expectedCount = 0) {
  const sorted = (Array.isArray(items) ? items : [])
    .filter((x) => x && x.id)
    .slice()
    .sort((a, b) => (a.at || 0) - (b.at || 0));
  const sessions = [];
  for (const item of sorted) {
    const cur = sessions[sessions.length - 1];
    if (!cur) {
      sessions.push([item]);
      continue;
    }
    const prev = cur[cur.length - 1];
    const gap = (item.at || 0) - (prev.at || 0);
    const sameManual = item.manualBatchId && prev.manualBatchId && item.manualBatchId === prev.manualBatchId;
    const waitingForExpected = expectedCount > 0 && cur.length < expectedCount && !cur.every((z) => z.scanned) && gap >= 0 && gap <= 2 * 60 * 60 * 1000;
    if (gap > gapMs && !sameManual && !waitingForExpected) {
      sessions.push([item]);
    } else {
      cur.push(item);
    }
  }
  return sessions;
}

/**
 * Stellt sicher, dass im aktiven Stapel keine alten Pakete von früheren Lieferungen (> 15 Min. Abstand)
 * oder bereits archivierte Pakete hängenbleiben. Ältere Sitzungen wandern automatisch in die Historie.
 */
function load() {
  const raw = loadRawActive();
  if (raw.length <= 1) return raw;
  const hist = loadHistoryRaw();
  const archivedIds = new Set();
  for (const h of hist) {
    for (const it of h.items || []) {
      if (it && it.id) archivedIds.add(it.id);
    }
  }
  const filtered = raw.filter((x) => x && x.id && (!archivedIds.has(x.id) || x.restoredAt));
  const sessions = splitItemsByTimeGap(filtered, BATCH_GAP_MS, getBatchExpectedCount());
  if (sessions.length <= 1 && filtered.length === raw.length) {
    return filtered;
  }
  const histById = new Map(hist.map((h) => [h.id, h]));
  if (sessions.length > 1) {
    for (let i = 0; i < sessions.length - 1; i++) {
      const chunk = sessions[i];
      const times = chunk.map((x) => x.at || Date.now()).filter(Boolean);
      const startedAt = times.length ? Math.min(...times) : Date.now();
      const finishedAt = times.length ? Math.max(...times) : Date.now();
      const scannedCount = chunk.filter((x) => x.scanned).length;
      const allScanned = chunk.length > 0 && scannedCount === chunk.length;
      const hid = `batch-${startedAt}-${chunk[0].id || i}`;
      if (!histById.has(hid)) {
        histById.set(hid, {
          id: hid,
          startedAt,
          finishedAt,
          count: chunk.length,
          scannedCount,
          completed: allScanned,
          completedAt: allScanned ? (Math.max(...chunk.map((x) => x.scannedAt || x.at || 0)) || finishedAt) : null,
          items: chunk,
        });
      }
    }
    saveHistoryRaw(Array.from(histById.values()).sort((a, b) => (b.finishedAt || 0) - (a.finishedAt || 0)));
  }
  const latest = sessions.length ? sessions[sessions.length - 1] : [];
  try {
    localStorage.setItem(KEY, JSON.stringify(latest.slice(-MAX)));
    const curPos = getBatchPos();
    if (curPos >= latest.length) {
      localStorage.setItem(KEY_POS, String(Math.max(0, latest.length - 1)));
    }
  } catch { /* */ }
  return latest;
}

function notifyChanged(list) {
  try { window.dispatchEvent(new CustomEvent('kiosk:batch-changed', { detail: (list || []).length })); } catch { /* */ }
}

// Hintergrund-Sync zum Server (damit Fotostation, Tablet & Handy denselben Account-Stapel teilen)
const BATCH_API_URL = `${SERVER_URL}/user/batch`;
let syncTimer = null;
export function pushBatchToServer() {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return;
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(async () => {
    const removeIds = Array.from(pendingRemovedIds);
    try {
      const token = localStorage.getItem('kiosk_jwt_access');
      if (!token) return;
      const deletedHistoryIds = Array.from(pendingDeletedHistoryIds);
      pendingDeletedHistoryIds.clear();
      pendingRemovedIds.clear();
      const r = await fetch(BATCH_API_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          active: load(),
          pos: getBatchPos(),
          expectedCount: getBatchExpectedCount(),
          history: loadHistoryRaw(),
          deletedHistoryIds,
          removeIds,
        }),
      });
      if (r.ok) {
        const data = await r.json().catch(() => null);
        if (data && Array.isArray(data.active)) {
          applyServerBatch(data);
        }
      } else {
        for (const id of removeIds) pendingRemovedIds.add(id);
      }
    } catch {
      /* offline / egal — gezielte Löschungen beim nächsten Push erneut mitschicken */
      for (const id of removeIds) pendingRemovedIds.add(id);
    }
  }, 250);
}

/** Historie zusammenführen (Server + Lokal): erledigt bzw. mehr gescannt gewinnt, lokal gelöschte bleiben weg */
function mergeHistoryLists(localHist, remoteHist, deletedIds = []) {
  const del = new Set(Array.isArray(deletedIds) ? deletedIds : []);
  const histById = new Map();
  for (const h of [...(localHist || []), ...(remoteHist || [])]) {
    if (!h || !h.id || pendingDeletedHistoryIds.has(h.id) || del.has(h.id)) continue;
    const prev = histById.get(h.id);
    // Falls einer der beiden Stände bereits als erledigt markiert ist, gewinnt erledigt
    if (!prev || (h.completed && !prev.completed) || (h.scannedCount || 0) > (prev.scannedCount || 0)) {
      histById.set(h.id, h);
    }
  }
  return Array.from(histById.values()).sort((a, b) => (b.finishedAt || 0) - (a.finishedAt || 0));
}

/**
 * Aktiven Stapel nach id zusammenführen: Server-Stand gewinnt bei gleicher id, lokale Einträge, die der
 * Server (noch) nicht kennt, bleiben erhalten (z. B. Scan während ein PUT unterwegs war). Bereits archivierte
 * Alt-Pakete werden NICHT wiederbelebt, bewusst entfernte (removedIds) nicht zurückgeholt.
 */
function mergeActiveLists(remoteList, localList, mergedHist, removedIds = []) {
  const removed = new Set(Array.isArray(removedIds) ? removedIds : []);
  const archivedItemIds = new Set();
  for (const h of mergedHist || []) {
    for (const it of h.items || []) {
      if (it && it.id) archivedItemIds.add(it.id);
    }
  }
  const byId = new Map();
  for (const x of remoteList || []) {
    if (x && x.id && !pendingRemovedIds.has(x.id) && (!archivedItemIds.has(x.id) || x.restoredAt)) byId.set(x.id, x);
  }
  for (const x of localList || []) {
    if (x && x.id && !byId.has(x.id) && !removed.has(x.id) && (!archivedItemIds.has(x.id) || x.restoredAt)) {
      byId.set(x.id, x);
    }
  }
  return Array.from(byId.values()).sort((a, b) => (a.at || 0) - (b.at || 0));
}

export function applyServerBatch(remote) {
  if (!remote || typeof remote !== 'object') return;
  try {
    // Nie blind ersetzen: Server-Stand mit lokalem Stand nach id zusammenführen (sonst gehen Scans verloren)
    const mergedHist = mergeHistoryLists(loadHistoryRaw(), Array.isArray(remote.history) ? remote.history : [], remote.deletedHistoryIds);
    if (Array.isArray(remote.history)) {
      saveHistoryRaw(mergedHist);
    }
    if (Array.isArray(remote.active)) {
      const mergedActive = mergeActiveLists(remote.active, loadRawActive(), mergedHist, remote.removedIds);
      localStorage.setItem(KEY, JSON.stringify(mergedActive.slice(-MAX)));
    }
    if (Number.isInteger(remote.pos)) {
      localStorage.setItem(KEY_POS, String(Math.max(0, remote.pos)));
    }
    if (Number.isInteger(remote.expectedCount)) {
      if (remote.expectedCount > 0) localStorage.setItem(KEY_EXPECTED, String(remote.expectedCount));
      else localStorage.removeItem(KEY_EXPECTED);
    }
    notifyChanged(load());
  } catch { /* */ }
}

export async function syncBatchFromServer() {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return;
  try {
    const token = localStorage.getItem('kiosk_jwt_access');
    if (!token) return;
    const r = await fetch(BATCH_API_URL, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return;
    const data = await r.json();
    const localList = loadRawActive();
    const remoteList = Array.isArray(data.active) ? data.active : [];
    const localHist = loadHistoryRaw();
    const remoteHist = Array.isArray(data.history) ? data.history : [];

    if (remoteList.length > 0 || remoteHist.length > 0 || localList.length === 0) {
      // 1) Historie zusammenführen (Server + Lokal)
      const mergedHist = mergeHistoryLists(localHist, remoteHist);

      // 2) + 3) Aktiven Stapel nach id zusammenführen, aber KEINE bereits archivierten Alt-Pakete wiederbeleben!
      const mergedActive = mergeActiveLists(remoteList, localList, mergedHist);

      saveHistoryRaw(mergedHist);
      localStorage.setItem(KEY, JSON.stringify(mergedActive.slice(-MAX)));
      if (Number.isInteger(data.expectedCount)) {
        if (data.expectedCount > 0) localStorage.setItem(KEY_EXPECTED, String(data.expectedCount));
        else if (!getBatchExpectedCount()) localStorage.removeItem(KEY_EXPECTED);
      }
      const cleanActive = load();
      if (Number.isInteger(data.pos) && data.pos >= 0) {
        localStorage.setItem(KEY_POS, String(Math.min(data.pos, Math.max(0, cleanActive.length - 1))));
      }
      notifyChanged(cleanActive);
    } else if (localList.length > 0) {
      pushBatchToServer();
    }
  } catch { /* ignore */ }
}

function save(list, sync = true) {
  try { localStorage.setItem(KEY, JSON.stringify(list.slice(-MAX))); } catch { /* */ }
  notifyChanged(list);
  if (sync) pushBatchToServer();
}

export function listBatch() { return load(); }

export function getBatchPos() {
  try {
    const p = parseInt(localStorage.getItem(KEY_POS) || '0', 10);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  } catch { return 0; }
}

export function setBatchPos(pos, sync = true) {
  const clean = Math.max(0, Number.isFinite(pos) ? Math.floor(pos) : 0);
  try { localStorage.setItem(KEY_POS, String(clean)); } catch { /* */ }
  if (sync) pushBatchToServer();
}

export function markBatchItemScanned(id, scanned = true) {
  const list = load();
  let changed = false;
  const next = list.map((item) => {
    if (item.id === id && !!item.scanned !== !!scanned) {
      changed = true;
      return { ...item, scanned: !!scanned, scannedAt: scanned ? Date.now() : undefined };
    }
    return item;
  });
  if (changed) save(next);
}

export function listBatchHistory() {
  return loadHistoryRaw().sort((a, b) => (b.finishedAt || 0) - (a.finishedAt || 0));
}

/**
 * Markiert den gesamten aktuellen Stapel auf einen Schlag als „✅ Erledigt" (mit Uhrzeit)
 * und legt ihn sauber als abgeschlossenen Stapel in die Stapel-Liste (Historie).
 */
export function completeCurrentBatch(now = Date.now()) {
  const list = load();
  if (!list.length) return null;
  const completedItems = list.map((x) => ({
    ...x,
    scanned: true,
    scannedAt: x.scannedAt || now,
  }));
  const expected = getBatchExpectedCount();
  const times = completedItems.map((x) => x.at || now).filter(Boolean);
  const finishedAt = times.length ? Math.max(...times) : now;
  const archivedId = archiveBatchInternal(completedItems, finishedAt, {
    completed: true,
    completedAt: now,
    completedManual: true,
    expectedCount: expected || undefined,
  });
  setBatchPos(0, false);
  setBatchExpectedCount(0, false);
  save([], true);
  return archivedId;
}

/**
 * Markiert einen früheren Stapel in der Historie als „✅ Erledigt" (mit Uhrzeit) oder setzt ihn wieder auf offen.
 */
export function markHistoryBatchCompleted(historyId, completed = true, now = Date.now()) {
  const hist = loadHistoryRaw();
  let found = false;
  const next = hist.map((h) => {
    if (!h || h.id !== historyId) return h;
    found = true;
    const updatedItems = (h.items || []).map((it) => ({
      ...it,
      scanned: !!completed,
      scannedAt: completed ? (it.scannedAt || now) : undefined,
    }));
    return {
      ...h,
      items: updatedItems,
      scannedCount: completed ? updatedItems.length : 0,
      completed: !!completed,
      completedAt: completed ? now : null,
      completedManual: !!completed,
    };
  });
  if (!found) return false;
  saveHistoryRaw(next);
  notifyChanged(load());
  pushBatchToServer();
  return true;
}

/**
 * Erstellt aus einer Paket-Lieferung (z. B. aus den erfassten Paketen der letzten 7 Tage)
 * direkt einen als „✅ Erledigt" markierten Stapel-Eintrag mit Uhrzeit in der Historie.
 */
export function markPackageGroupCompleted(pakete = [], now = Date.now()) {
  const items = buildBatchItemsFromPackages(pakete, now).map((it) => ({
    ...it,
    scanned: true,
    scannedAt: now,
  }));
  if (!items.length) return null;
  const times = items.map((x) => x.at || now).filter(Boolean);
  const finishedAt = times.length ? Math.max(...times) : now;
  const id = archiveBatchInternal(items, finishedAt, {
    completed: true,
    completedAt: now,
    completedManual: true,
  });
  notifyChanged(load());
  pushBatchToServer();
  return id;
}

/**
 * Stellt einen Stapel aus der 7-Tage-Historie als aktuellen Stapel wieder her.
 */
export function restoreBatch(historyId) {
  const hist = loadHistoryRaw();
  const target = historyId ? hist.find((h) => h.id === historyId) : hist[0];
  if (!target || !Array.isArray(target.items) || !target.items.length) return false;
  const current = loadRawActive();
  // Falls aktuell ein anderer Stapel offen ist, diesen zuerst in die Historie sichern
  if (current.length > 0) {
    const times = current.map((x) => x.at || Date.now()).filter(Boolean);
    const fin = times.length ? Math.max(...times) : Date.now();
    const allScanned = current.every((x) => x.scanned);
    archiveBatchInternal(current, fin, {
      completed: allScanned,
      completedAt: allScanned ? fin : null,
      expectedCount: getBatchExpectedCount() || undefined,
    });
  }
  const now = Date.now();
  const manualBatchId = `restored-${now}`;
  const restoredItems = target.items.map((x) => ({
    ...x,
    scanned: !!x.scanned,
    scannedAt: x.scannedAt || undefined,
    restoredAt: now,
    manualBatchId,
  }));
  pendingDeletedHistoryIds.add(target.id);
  const nextHist = loadHistoryRaw().filter((h) => h.id !== target.id);
  saveHistoryRaw(nextHist);
  if (target.expectedCount > 0) setBatchExpectedCount(target.expectedCount, false);
  else setBatchExpectedCount(0, false);
  const firstOpenIdx = restoredItems.findIndex((x) => !x.scanned);
  setBatchPos(firstOpenIdx >= 0 ? firstOpenIdx : 0, false);
  save(restoredItems, true);
  return true;
}

/**
 * Verbindet einen früheren Stapel aus der Historie mit dem aktuellen Stapel (falls man z. B.
 * während einer Lieferung länger als 15 Min. durch Kunden unterbrochen wurde).
 */
export function mergeHistoryIntoActive(historyId) {
  const hist = loadHistoryRaw();
  const target = hist.find((h) => h && h.id === historyId);
  if (!target || !Array.isArray(target.items) || !target.items.length) return false;
  const current = loadRawActive();
  const now = Date.now();
  const manualBatchId = `merged-${now}`;
  const byId = new Map();
  for (const x of [...target.items, ...current]) {
    if (x && x.id) {
      byId.set(x.id, { ...x, restoredAt: now, manualBatchId });
    }
  }
  const merged = Array.from(byId.values()).sort((a, b) => (a.at || 0) - (b.at || 0));
  pendingDeletedHistoryIds.add(target.id);
  saveHistoryRaw(hist.filter((h) => h.id !== target.id));
  const firstOpenIdx = merged.findIndex((x) => !x.scanned);
  setBatchPos(firstOpenIdx >= 0 ? firstOpenIdx : 0, false);
  save(merged, true);
  return true;
}

/**
 * Setzt den Gescannt-Status aller Pakete im aktuellen Stapel zurück (falls man nochmal von vorne scannen will).
 */
export function resetBatchScanned() {
  const list = load().map((x) => ({ ...x, scanned: false, scannedAt: undefined }));
  setBatchPos(0, false);
  save(list, true);
}

/**
 * Deaktiviert die Sonderfunktion „batchBarcodes" (Barcode-Stapel beim Erfassen nicht mehr anzeigen).
 * Kann jederzeit über das Setup (Einstellungen → Setup starten → Pakete erfassen) wieder aktiviert werden.
 */
export async function disableBatchBarcodesFeature() {
  try {
    localStorage.setItem('kiosk_batch_barcodes_disabled', '1');
    const raw = JSON.parse(localStorage.getItem('kiosk_custom_features') || '{}') || {};
    const next = { ...raw, batchBarcodes: false };
    localStorage.setItem('kiosk_custom_features', JSON.stringify(next));
    window.dispatchEvent(new CustomEvent('kiosk:custom-features', { detail: next }));
    window.dispatchEvent(new CustomEvent('kiosk:batch-changed'));
  } catch { /* */ }
  try {
    const prof = JSON.parse(localStorage.getItem('kiosk_profile') || 'null');
    if (prof && typeof prof === 'object') {
      const nextFeatures = { ...(prof.features || {}) };
      delete nextFeatures.batchBarcodes;
      prof.features = nextFeatures;
      localStorage.setItem('kiosk_profile', JSON.stringify(prof));
    }
  } catch { /* */ }
  try {
    const { saveAccountProfile } = await import('./serverDataService');
    await saveAccountProfile({ features: { batchBarcodes: false } });
  } catch { /* offline / egal */ }
}

function buildBatchItemsFromPackages(pakete = [], now = Date.now()) {
  const items = [];
  const manualBatchId = `pkgbatch-${now}`;
  for (const p of pakete || []) {
    if (!p) continue;
    let trk = String(p.trackingNumber || p.trackingNummer || p.trackingId || '').trim();
    if (trk.toLowerCase() === 'keine') trk = '';
    const plz = String(p.postalAndCity || '').trim();
    const rawCodes = Array.isArray(p.batchCodes) ? p.batchCodes : [];
    const c = ergaenzeStrichcode(dedupeCodes(rawCodes, plz), trk, plz, true);
    if (!c.length) continue;
    items.push({
      id: p.id || `${now}-${Math.random().toString(36).slice(2, 7)}`,
      paketId: p.paketId || p.id || null,
      imageId: p.imageUrl || p.image_url || p.paketId || p.id || null,
      name: String(p.name || p.paketName || '').trim(),
      regalplatz: String(p.regalplatz || '').trim(),
      tracking: trk,
      codes: c,
      best: Math.max(0, besterCodeIndex(c, trk)),
      at: p.timestamp || now,
      scanned: false,
      restoredAt: now,
      manualBatchId,
    });
  }
  return items;
}

/**
 * Erstellt aus einer Liste von Paketen (z. B. aus der Chronik / Lieferungs-Liste)
 * direkt einen abarbeitbaren Barcode-Stapel.
 */
export function openBatchFromPackages(pakete = []) {
  const now = Date.now();
  const items = buildBatchItemsFromPackages(pakete, now);
  if (!items.length) return 0;
  // Falls aktuell ein Stapel offen ist, vorher archivieren
  const cur = loadRawActive();
  if (cur.length > 0) {
    const times = cur.map((x) => x.at || now).filter(Boolean);
    const fin = times.length ? Math.max(...times) : now;
    const allScanned = cur.every((x) => x.scanned);
    archiveBatchInternal(cur, fin, {
      completed: allScanned,
      completedAt: allScanned ? fin : null,
      expectedCount: getBatchExpectedCount() || undefined,
    });
  }
  setBatchPos(0, false);
  setBatchExpectedCount(0, false);
  save(items, true);
  return items.length;
}

function archiveBatchInternal(list, now = Date.now(), meta = {}) {
  if (!Array.isArray(list) || !list.length) return null;
  const times = list.map((x) => x.at || now).filter(Boolean);
  const startedAt = times.length ? Math.min(...times) : now;
  const finishedAt = times.length ? Math.max(...times) : now;
  const scannedCount = list.filter((x) => x.scanned).length;
  const allScanned = scannedCount === list.length;
  const completed = meta.completed !== undefined ? !!meta.completed : allScanned;
  const completedAt = completed ? (meta.completedAt || now) : null;
  const entry = {
    id: `batch-${startedAt}-${list[0]?.id || Math.random().toString(36).slice(2, 6)}`,
    startedAt,
    finishedAt,
    count: list.length,
    scannedCount,
    completed,
    completedAt,
    completedManual: !!meta.completedManual,
    expectedCount: meta.expectedCount || undefined,
    items: list,
  };
  const hist = loadHistoryRaw().filter((h) => h && h.id !== entry.id);
  hist.unshift(entry);
  saveHistoryRaw(hist);
  return entry.id;
}

/**
 * Erzeugt einen 1D-Strichcode (Code 128 / GS1-128), falls im Foto nur ein 2D-Code
 * (z. B. DHLDMC|0034…, Deutsche-Post-DataMatrix oder Mondial Relay) gelesen wurde
 * ODER falls der Strichcode im Foto abgeschnitten/beschädigt war, die DHL-Sendungsnummer
 * aber eine gültige Modulo-10-Prüfziffer besitzt.
 */
export function ergaenzeStrichcode(codes, tracking = '', plz = '', manuellBestaetigt = false) {
  const c = codes || [];
  const rawTrk = String(tracking || '').trim();
  const chk = pruefeDhlSendungsnummer(rawTrk);

  // Wenn vom Nutzer im Fallback manuell bestätigt/eingegeben: sicherstellen, dass genau diese
  // Sendungsnummer als 1D-Code-128 im Stapel liegt (auch bei exotischen/internationalen Nummern)
  if (manuellBestaetigt && rawTrk.length >= 4) {
    const norm = chk.valid ? chk.normalized : rawTrk.replace(/\s+/g, '');
    const codeText = /^0034\d{16}$/.test(norm) ? `(00)${norm.slice(2)}` : norm;
    const schonDa = c.some((x) => EINDIM.has(normFormat(x.format)) && alnum(x.text).includes(alnum(norm)));
    if (!schonDa) {
      return [{ text: codeText, format: 'code_128', abgeleitet: true }, ...c];
    }
    return c;
  }

  if (c.some((x) => EINDIM.has(normFormat(x.format)))) return c;

  // 1) Aus vorhandenem 2D-Code (z. B. DHLDMC|0034… oder |V1.0|3586…|) die Sendungsnummer holen
  for (const x of c) {
    const extr = extrahiereSendungsnummerAusBarcode(x, plz);
    if (extr) {
      const codeText = /^0034\d{16}$/.test(extr) ? `(00)${extr.slice(2)}` : extr;
      return [...c, { text: codeText, format: 'code_128', abgeleitet: true }];
    }
  }

  // 2) Kurzer 2D-Code (z. B. Mondial Relay)
  const kandidat = c.find((x) => /^[ -~]{6,40}$/.test(String(x.text || '')) && !String(x.text || '').includes('|'));
  if (kandidat) {
    return [...c, { text: kandidat.text, format: 'code_128', abgeleitet: true }];
  }

  // 3) Kein Barcode im Foto lesbar (z. B. Druckfehler auf Label), aber DHL-/UPU-S10-Prüfziffer der OCR-Nummer ist gültig
  if (chk.valid) {
    const codeText = /^0034\d{16}$/.test(chk.normalized) ? `(00)${chk.normalized.slice(2)}` : chk.normalized;
    return [...c, { text: codeText, format: 'code_128', abgeleitet: true }];
  }

  return c;
}

export function addToBatch({ name, regalplatz, tracking, codes, plz = '', manuellBestaetigt = false, paketId = null, imageId = null, dienst = '' }, now = Date.now()) {
  // Ohne expliziten Paketdienst: rein numerische Sendungsnummer → DHL-Leitcode-Filter wie bisher, sonst keine Codes verwerfen
  const dienstEff = dienst || (pruefeDhlSendungsnummer(String(tracking || '')).isNumericDhl ? 'DHL' : '');
  const abgleich = doppelabgleichSendung({ codes, tracking, plz, manuellBestaetigt, dienst: dienstEff });
  const finalTracking = abgleich.tracking || String(tracking || '').trim();
  const c = ergaenzeStrichcode(dedupeCodes(codes, plz, dienstEff), finalTracking, plz, manuellBestaetigt);
  if (!c.length) return false;
  let list = load();
  if (list.length > 0) {
    const lastAt = Math.max(...list.map((x) => x.at || 0));
    const gapMs = now - lastAt;
    const allScanned = list.every((x) => x.scanned);
    const expected = getBatchExpectedCount();
    // Falls ein aktiver Boten-Zähler (Soll-Anzahl) für diese Lieferung läuft (< 2 Std.) und noch nicht voll ist,
    // bleiben die Pakete im selben Stapel — ansonsten trennt ein Abstand > 15 Minuten automatisch in einen neuen Stapel!
    const waitingForExpected = expected > 0 && list.length < expected && !allScanned && gapMs >= 0 && gapMs <= 2 * 60 * 60 * 1000;
    if (allScanned || (gapMs > BATCH_GAP_MS && !waitingForExpected)) {
      archiveBatchInternal(list, lastAt || now, {
        completed: allScanned,
        completedAt: allScanned ? (Math.max(...list.map((x) => x.scannedAt || x.at || 0)) || lastAt) : null,
        expectedCount: expected || undefined,
      });
      list = [];
      setBatchPos(0, false);
      setBatchExpectedCount(0, false);
    }
  }
  const activeManualBatchId = list.length > 0 && list[list.length - 1]?.manualBatchId
    ? list[list.length - 1].manualBatchId
    : undefined;
  list.push({
    id: `${now}-${Math.random().toString(36).slice(2, 7)}`,
    paketId: paketId || null,
    imageId: imageId || paketId || null,
    name: String(name || '').trim(),
    regalplatz: String(regalplatz || '').trim(),
    tracking: finalTracking,
    codes: c,
    best: Math.max(0, besterCodeIndex(c, finalTracking)),
    at: now,
    scanned: false,
    ...(activeManualBatchId ? { manualBatchId: activeManualBatchId } : {}),
  });
  save(list);
  return true;
}

export function removeFromBatch(id) {
  const list = load();
  const idx = list.findIndex((e) => e.id === id);
  const next = list.filter((e) => e.id !== id);
  const curPos = getBatchPos();
  if (idx >= 0 && idx < curPos) {
    setBatchPos(Math.max(0, curPos - 1), false);
  } else if (curPos >= next.length) {
    setBatchPos(Math.max(0, next.length - 1), false);
  }
  if (idx >= 0) pendingRemovedIds.add(id);
  save(next);
}

export function clearBatch(now = Date.now()) {
  const current = load();
  let archivedId = null;
  if (current.length > 0) {
    const times = current.map((x) => x.at || now).filter(Boolean);
    const fin = times.length ? Math.max(...times) : now;
    const allScanned = current.every((x) => x.scanned);
    archivedId = archiveBatchInternal(current, fin, {
      completed: allScanned,
      completedAt: allScanned ? now : null,
      expectedCount: getBatchExpectedCount() || undefined,
    });
  }
  setBatchPos(0, false);
  setBatchExpectedCount(0, false);
  save([], true);
  return archivedId;
}

// ---------------------------------------------------------------------------
// Code 128 (eigener Encoder — die zxing-JS-Bibliothek kann nur QR/Data Matrix schreiben)
// ---------------------------------------------------------------------------
const C128 = ['212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112'];
const START_A = 103, START_B = 104, START_C = 105, STOP = 106;
const CODE_A = 101, CODE_B = 100, CODE_C = 99, FNC1 = 102;

/**
 * Text → Code-128-Symbolwerte (inkl. Start + Prüfziffer + Stopp).
 * GS1-128: führendes „]C1", „(00)" + 18 Ziffern (NVE/SSCC) oder enthaltene GS-Zeichen (\x1d) → FNC1.
 */
export function code128Values(input) {
  let text = String(input || '');
  let gs1 = false;
  if (text.startsWith(']C1')) { gs1 = true; text = text.slice(3); }
  const mNve = text.match(/^\(00\)(\d{18})$/);
  if (mNve) { gs1 = true; text = '00' + mNve[1]; }
  else if (/^0034\d{16}$/.test(text)) { gs1 = true; }
  if (text.includes('\x1d')) gs1 = true;
  const isD = (i) => i < text.length && text.charCodeAt(i) >= 48 && text.charCodeAt(i) <= 57;
  const digitRun = (i) => { let n = 0; while (isD(i + n)) n++; return n; };
  const ctl = (ch) => ch.charCodeAt(0) < 32;
  const vals = [];
  let set;
  const run0 = digitRun(0);
  if (run0 >= 4 && (run0 % 2 === 0 || run0 === text.length)) { set = 'C'; vals.push(START_C); }
  else if (text.length && ctl(text[0]) && text[0] !== '\x1d') { set = 'A'; vals.push(START_A); }
  else { set = 'B'; vals.push(START_B); }
  if (gs1) vals.push(FNC1);

  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (gs1 && ch === '\x1d') { vals.push(FNC1); i++; continue; }
    if (set === 'C') {
      if (isD(i) && isD(i + 1)) { vals.push(Number(text.substr(i, 2))); i += 2; continue; }
      if (ctl(ch)) { vals.push(CODE_A); set = 'A'; } else { vals.push(CODE_B); set = 'B'; }
      continue;
    }
    // A oder B: lohnt der Wechsel nach C? (≥4 Ziffern am Ende, sonst ≥6)
    const run = digitRun(i);
    if (run >= 6 || (run >= 4 && i + run === text.length)) {
      if (run % 2 === 1) { vals.push(set === 'A' ? ch.charCodeAt(0) - 32 : ch.charCodeAt(0) - 32); i++; }
      vals.push(CODE_C); set = 'C';
      continue;
    }
    const code = ch.charCodeAt(0);
    if (code > 127) { vals.push(set === 'A' ? 31 : 31); i++; continue; } // '?' als Notlösung
    if (set === 'B') {
      if (code < 32) { vals.push(CODE_A); set = 'A'; continue; }
      vals.push(code - 32);
    } else { // A
      if (code >= 96) { vals.push(CODE_B); set = 'B'; continue; }
      vals.push(code < 32 ? code + 64 : code - 32);
    }
    i++;
  }
  let sum = vals[0];
  for (let k = 1; k < vals.length; k++) sum += vals[k] * k;
  vals.push(sum % 103);
  vals.push(STOP);
  return vals;
}

/** Symbolwerte → Modulbreiten (Balken/Lücke abwechselnd, beginnend mit Balken) */
export function code128Widths(input) {
  const w = [];
  for (const v of code128Values(input)) for (const d of C128[v]) w.push(Number(d));
  return w;
}
